import type { KeyboardEvent } from "react";
import { useRef, useState } from "react";

/**
 * Draft-until-commit editing for a title input: Enter/blur commit, Escape cancels.
 * The committed value is trimmed, with an empty result mapped to `null` (clear the
 * title); an unchanged value doesn't commit at all.
 */
export function useInlineTitleEdit(currentTitle: string | null, onCommit: (title: string | null) => void) {
  const [draft, setDraftState] = useState<string | null>(null);
  // Mirrors the draft state so commit stays idempotent when Enter's explicit commit
  // is followed synchronously by the blur it triggers — the blur handler's closure
  // still sees the pre-commit state, but the ref is already cleared.
  const draftRef = useRef<string | null>(null);

  const setDraft = (value: string | null) => {
    draftRef.current = value;
    setDraftState(value);
  };

  const beginEdit = () => setDraft(currentTitle ?? "");

  const commitDraft = () => {
    const value = draftRef.current;
    if (value === null) return;
    setDraft(null);
    // Before normalizing, bail on a verbatim-unchanged draft — otherwise opening
    // and blurring a whitespace-padded title would commit its trimmed form.
    if (value === currentTitle) return;
    const trimmed = value.trim();
    const nextTitle = trimmed.length > 0 ? trimmed : null;
    if (nextTitle !== currentTitle) onCommit(nextTitle);
  };

  const handleBlur = () => commitDraft();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      commitDraft();
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      setDraft(null);
      event.currentTarget.blur();
    }
  };

  return { draft, setDraft, beginEdit, isEditing: draft !== null, handleBlur, handleKeyDown };
}
